/**
 * Platform headers (U-14). Every backend call from the store builds says which app made it:
 *
 *   X-Lighthouse-Platform: ios | android
 *   X-Lighthouse-App-Version: <App.getInfo().version>
 *
 * The backend uses them to refuse purchases from the apps (403 `web_only`), to word 402/429 bodies neutrally,
 * and to record the client on the heartbeat. The web sends nothing (the contract allows "web" or nothing).
 *
 * The authenticated axios client adds them itself (src/api/auth-http-client.ts). `installNativePlatformHeaders()`
 * covers everything else that talks to the backend: the default axios instance, the public axios clients, and
 * `fetch` (streams, bulk sends, voice) — only for requests to `VITE_APP_BACKEND_URL`.
 */
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { getNativePlatform, isNativePlatform } from '@/utils/platform'

export const PLATFORM_HEADER = 'X-Lighthouse-Platform'
export const APP_VERSION_HEADER = 'X-Lighthouse-App-Version'

export interface NativeAppInfo {
    version: string
    build: string
}

/** The headers for a platform and binary version. Web → none. */
export function buildPlatformHeaders(platform: string, version: string | null | undefined): Record<string, string> {
    if (platform !== 'ios' && platform !== 'android') return {}
    const headers: Record<string, string> = { [PLATFORM_HEADER]: platform }
    if (version) headers[APP_VERSION_HEADER] = version
    return headers
}

/** True when `url` goes to the backend origin (absolute URL with the same origin, or under the base path). */
export function isBackendUrl(url: string, backendBase: string | null | undefined): boolean {
    if (!url || !backendBase) return false
    let target: URL
    let base: URL
    try {
        base = new URL(backendBase)
        target = new URL(url, base)
    } catch {
        return false
    }
    if (target.origin !== base.origin) return false
    const basePath = base.pathname.replace(/\/+$/, '')
    return !basePath || target.pathname === basePath || target.pathname.startsWith(`${basePath}/`)
}

let info: NativeAppInfo | null = null
let infoPromise: Promise<NativeAppInfo | null> | null = null

/** `App.getInfo()` once per launch (null on the web or without the plugin). */
export function loadNativeAppInfo(): Promise<NativeAppInfo | null> {
    if (info) return Promise.resolve(info)
    if (!isNativePlatform() || !Capacitor.isPluginAvailable('App')) return Promise.resolve(null)
    if (!infoPromise) {
        infoPromise = App.getInfo()
            .then((i) => {
                info = { version: String(i.version || ''), build: String(i.build || '') }
                return info
            })
            .catch(() => {
                infoPromise = null
                return null
            })
    }
    return infoPromise
}

/** The binary's version once known (null before the first `loadNativeAppInfo()` settles, and on the web). */
export function nativeAppInfo(): NativeAppInfo | null {
    return info
}

/** Headers for this device right now. */
export async function platformHeaders(): Promise<Record<string, string>> {
    if (!isNativePlatform()) return {}
    const i = await loadNativeAppInfo()
    return buildPlatformHeaders(getNativePlatform(), i?.version)
}

/** Add the headers to an axios request config (used by every interceptor below). */
export async function withPlatformHeaders<T extends InternalAxiosRequestConfig>(config: T): Promise<T> {
    const headers = await platformHeaders()
    for (const [key, value] of Object.entries(headers)) {
        if (config.headers && !config.headers[key]) config.headers[key] = value
    }
    return config
}

const tagged = new WeakSet<AxiosInstance>()

/** Native only: tag every request from this axios instance. Idempotent. */
export function tagAxiosInstance(instance: AxiosInstance): void {
    if (!isNativePlatform() || tagged.has(instance)) return
    tagged.add(instance)
    instance.interceptors.request.use((config) => withPlatformHeaders(config))
}

let fetchInstalled = false

/** Native only: `fetch` calls to the backend carry the headers. */
export function installPlatformFetchHeaders(backendBase: string | null | undefined = import.meta.env.VITE_APP_BACKEND_URL as string | undefined): void {
    if (fetchInstalled || !isNativePlatform() || typeof window === 'undefined' || typeof window.fetch !== 'function' || !backendBase) return
    fetchInstalled = true
    const original = window.fetch.bind(window)
    window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
        if (!isBackendUrl(url, backendBase)) return original(input, init)
        const extra = await platformHeaders()
        if (!Object.keys(extra).length) return original(input, init)
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
        for (const [key, value] of Object.entries(extra)) {
            if (!headers.has(key)) headers.set(key, value)
        }
        return original(input, { ...init, headers })
    }) as typeof window.fetch
}

/** Call once at boot (src/native/nativeApp.ts). */
export function installNativePlatformHeaders(defaultAxios?: AxiosInstance): void {
    if (!isNativePlatform()) return
    void loadNativeAppInfo()
    installPlatformFetchHeaders()
    if (defaultAxios) tagAxiosInstance(defaultAxios)
}

/** Test-only. */
export function __resetPlatformHeadersForTests(): void {
    info = null
    infoPromise = null
    fetchInstalled = false
}
